import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../../components/ui/Breadcrumb";
import ThemeToggle from "../../components/ui/ThemeToggle";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

const Header = ({
  title,
  description,
  className = "",
  // Optional: allow override of default navigation
  customNavigationItems = null,
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Default navigation items - can be overridden with customNavigationItems prop
  const defaultNavigationItems = [
    {
      label: t("navigation.processingQueue"),
      path: "/invoice-processing-queue",
      icon: "Clock",
      description: t("navigation.processingQueueDescription"),
    },
    {
      label: t("navigation.errorCorrection"),
      path: "/invoice-error-correction",
      icon: "AlertTriangle",
      description: t("navigation.errorCorrectionDescription"),
    },
    {
      label: t("navigation.batchOverview"),
      path: "/upload-invoice",
      icon: "Layers",
      description: t("navigation.batchOverviewDescription"),
    },
    {
      label: t("navigation.completionSummary"),
      path: "/processing-completion-summary",
      icon: "CheckCircle",
      description: t("navigation.completionSummaryDescription"),
    },
  ];

  // Use custom navigation items if provided, otherwise use default
  const navigationItems = customNavigationItems || defaultNavigationItems;

  // Navigation helpers
  const isActivePath = (path) => {
    // Handle exact match and nested paths
    if (path === location?.pathname) return true;

    // For nested routes, check if current path starts with the nav path
    if (location?.pathname?.startsWith(path) && path !== "/") return true;

    return false;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Get active navigation item
  const activeNavItem = navigationItems.find((item) => isActivePath(item.path));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8 ${className}`}
    >
      <div className="flex flex-col space-y-6">
        {/* Header Title and Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {/* Active tab icon */}
              {activeNavItem && (
                <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                  <Icon
                    name={activeNavItem.icon}
                    size={18}
                    className="text-primary"
                    strokeWidth={2}
                  />
                </div>
              )}
              <h1 className="text-2xl font-semibold text-foreground">
                {title}
              </h1>
            </div>
            {description && (
              <p className="text-sm text-muted-foreground ms-11">
                {description}
              </p>
            )}
            {/* Active section indicator */}
            {activeNavItem && (
              <div className="flex items-center gap-2 mt-2 ms-11">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-xs font-medium text-primary uppercase tracking-normal">
                  {activeNavItem.label}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Breadcrumb />
            <ThemeToggle />
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                className="h-10 w-10"
              >
                <Icon
                  name={isMobileMenuOpen ? "X" : "Menu"}
                  size={20}
                  color="currentColor"
                />
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1 flex-wrap">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-200 ease-out hover:scale-[1.02] hover:shadow-sm
                    ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/20"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/70"
                    }
                  `}
                  title={item?.description}
                >
                  <Icon
                    name={item?.icon}
                    size={16}
                    color="currentColor"
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                  <span className={isActive ? "font-semibold" : ""}>
                    {item?.label}
                  </span>
                  {isActive && (
                    <motion.div
                      className="absolute -bottom-1 start-1/2 transform -translate-x-1/2 rtl:translate-x-1/2 w-2 h-0.5 bg-primary-foreground rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: 8 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden border-t border-gray-100 dark:border-gray-700 pt-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="space-y-1">
              {navigationItems?.map((item) => {
                const isActive = isActivePath(item?.path);
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 text-start rounded-lg
                      transition-all duration-200 ease-out
                      ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/70"
                      }
                    `}
                  >
                    <div
                      className={`
                      flex items-center justify-center w-8 h-8 rounded-md
                      ${
                        isActive
                          ? "bg-primary-foreground/20"
                          : "bg-muted group-hover:bg-muted-foreground/10"
                      }
                    `}
                    >
                      <Icon
                        name={item?.icon}
                        size={18}
                        color="currentColor"
                        strokeWidth={isActive ? 2.5 : 2}
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <span
                        className={`text-sm ${isActive ? "font-semibold" : "font-medium"}`}
                      >
                        {item?.label}
                      </span>
                      <span className="text-xs opacity-75 mt-0.5">
                        {item?.description}
                      </span>
                    </div>
                    {isActive && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </nav>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default Header;
